import { AbstractDialog } from "../abstract_dialog.js";
import { ConfigSyncService } from "../../../config_sync/config_sync_service.js";
import { DialogManager } from "../dialog_manager.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { UITextElement, MyTextAlign, MyTextVerticalAlign } from "../../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { DialogId } from "../dialog_id.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { resetLinkedSocialProviders } from "../account_providers_preview_state.js";
import { performLogin } from "../../../api/perform_login.js";
import { AuthSessionStore } from "../../../api/auth_session.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { clearFacebookAvatarCache } from "../../../common/facebook_avatar_loader.js";
import { FacebookSignInPlugin } from "../../../plugins/facebook_sign_in_plugin.js";
import { GoogleSignInPlugin } from "../../../plugins/play_games_plugin.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { applePlugin } from "../../../api/account_link_service.js";
import { AuthMessageDialog } from "./auth_message_dialog.js";
import { OfflineJournal } from "../../../offline/offline_journal.js";
import { Vector2 } from "../../../common/vector2.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { audioController } from "../../../audio/audio_controller.js";
import { deleteAccount } from "../../../api/requests/auth.requests.js";
import { DeleteAccountNotification } from "./delete_account_notification.js";
import { CumulativeBoosterService } from "../../../cumulative_boosters/cumulative_booster_service.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";

export class DeleteAccountDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.DELETE_ACCOUNT_DIALOG;

  private static readonly COUNTDOWN_SECONDS = 5;
  private static readonly BODY_H_COLLAPSED_DESIGN = 1034;
  private static readonly BODY_H_EXPANDED_DESIGN  = 1146;
  private static readonly BTN_Y_DESIGN            = 861;
  private static readonly BTN_Y_EXPANDED_DESIGN   = 973;

  private bg!: UINineSliceElement;
  private close_btn!: UIImage;
  private cancel_btn!: RoundedRectangle;
  private delete_btn!: RoundedRectangle;
  private is_expanded: boolean = false;
  private is_deleting = false;
  private confirmation_built: boolean = false;
  private is_confirmed: boolean = false;
  private mark_image!: UIImage;
  private delete_btn_label!: UITextElement;
  private countdown_start: number = -1;
  private countdown_last_shown: number = -1;

  constructor()
  {
    const design_body_w = 910;

    const body_w = LayoutScaler.widthFromDesignAdaptive( design_body_w );
    const body_h = LayoutScaler.heightFromDesign( DeleteAccountDialog.BODY_H_COLLAPSED_DESIGN );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createBackground();
    this.createTopBgPart();
    this.createWarningIcon();
    this.createTitle();
    this.createText();
    this.createActionButtons();
    this.createCloseButton();
  }

  private createBackground(): void
  {
    this.bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( 0 ),
      LayoutScaler.yFromDesign( 0 ),
      this.rect.getW(),
      this.rect.getH(),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( this.bg );
  }

  private createTopBgPart(): void
  {
    const design_w = 237;
    const design_h = 128;
    const design_y = -80;

    const part_w = LayoutScaler.widthFromDesign( design_w );
    const part_h = LayoutScaler.heightFromDesign( design_h );
    const part_x = this.rect.getW2() - part_w / 2;
    const part_y = LayoutScaler.yFromDesign( design_y );

    const bg_part = new UIImage(
      this.ctx,
      part_x,
      part_y,
      part_w,
      part_h,
      ImgID.UI_CapsuleTopBgPart,
      {},
      false
    );
    this.addChild( bg_part );
  }

  private createWarningIcon(): void
  {
    const design_w = 160;
    const design_h = 141;
    const design_y = -29;

    const icon_w = LayoutScaler.widthFromDesign( design_w );
    const icon_h = LayoutScaler.heightFromDesign( design_h );
    const icon_x = this.rect.getW2() - icon_w / 2;
    const icon_y = LayoutScaler.yFromDesign( design_y );

    const warning_icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_w,
      icon_h,
      ImgID.UI_WarningIcon,
      {},
      false
    );
    this.addChild( warning_icon );
  }

  private createTitle(): void
  {
    const design_h = 158;
    const design_y = 120;
    const design_font_size = 60;
    const design_outline_w = 20;

    const label_w = this.rect.getW() * 0.8;
    const label_h = LayoutScaler.heightFromDesign( design_h );
    const label_x = this.rect.getW2() - label_w / 2;
    const label_y = LayoutScaler.yFromDesign( design_y );

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "DELETE_ACCOUNT_DIALOG.TITLE",
        text_color: "#FFFA71",
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        auto_wrap: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.booster_dialog_title_outline,
      }
    );

    this.addChild( label );
  }

  private createText(): void
  {
    const design_w = 760;
    const design_h = 546;
    const design_y = 270;
    const design_font_size = 50;
    const design_outline_w = 10;

    const label_w = LayoutScaler.widthFromDesign( design_w );
    const label_h = LayoutScaler.heightFromDesign( design_h );
    const label_x = this.rect.getW2() - label_w / 2;
    const label_y = LayoutScaler.yFromDesign( design_y );

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "DELETE_ACCOUNT_DIALOG.WARNING",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        auto_wrap: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.settings_dialog_title_outline,
      }
    );

    this.addChild( label );
  }

  private createActionButtons(): void
  {
    const design_btn_w = 380;
    const design_btn_h = 112;
    const design_gap = 24;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );
    const gap   = LayoutScaler.widthFromDesign( design_gap );
    const total_w = btn_w * 2 + gap;
    const start_x = this.rect.getW2() - total_w / 2;
    const btn_y = LayoutScaler.yFromDesign( DeleteAccountDialog.BTN_Y_DESIGN );

    this.cancel_btn = new RoundedRectangle(
      this.ctx,
      start_x,
      btn_y,
      btn_w,
      btn_h,
      btn_h / 2,
      "#10B2E2",
      undefined,
      true
    );
    this.cancel_btn.addChild( this.actionButtonText( "CANCEL", btn_w, btn_h ), true );
    this.cancel_btn.setOnClick( () => { this.close(); } );
    this.addChild( this.cancel_btn );

    this.delete_btn = new RoundedRectangle(
      this.ctx,
      start_x + btn_w + gap,
      btn_y,
      btn_w,
      btn_h,
      btn_h / 2,
      "#F2A618",
      undefined,
      true
    );
    this.delete_btn.setOnClick( () =>
    {
      audioController.playSound( AudioClips.UI_CLICK );
      this.expandDialog();
    } );
    this.delete_btn_label = this.actionButtonText( "DELETE_ACCOUNT_DIALOG.BUTTON_TEXT", btn_w, btn_h );
    this.delete_btn.addChild( this.delete_btn_label, true );
    this.addChild( this.delete_btn );
  }

  private actionButtonText( localization_key: string, btn_w: number, btn_h: number ): UITextElement
  {
    const design_font_size = 47;

    return new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        using_y_offset: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 117;
    const design_offset_x = 10;
    const design_offset_y = 100;

    const btn_size = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_x = this.rect.getW() - btn_size + LayoutScaler.widthFromDesign( design_offset_x );
    const btn_y = -LayoutScaler.yFromDesign( design_offset_y );

    this.close_btn = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_size,
      btn_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );

    this.close_btn.setOnClick( () => this.close() );
    this.addChild( this.close_btn );
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  private createConfirmation(): void
  {
    if ( this.confirmation_built ) return;
    this.confirmation_built = true;

    const design_sub_size = 70;
    const design_text_w = 350;
    const design_text_gap = 20;
    const design_sub_y = 854;

    const sub_w = LayoutScaler.widthFromDesign( design_sub_size );
    const sub_h = LayoutScaler.heightFromDesign( design_sub_size );
    const text_w = LayoutScaler.widthFromDesign( design_text_w );
    const text_gap = LayoutScaler.widthFromDesign( design_text_gap );
    const row_w = sub_w + text_gap + text_w;

    const sub_x = this.rect.getW2() - row_w / 2;
    const sub_y = LayoutScaler.yFromDesign( design_sub_y );

    const substrate = new UIImage(
      this.ctx,
      sub_x,
      sub_y,
      sub_w,
      sub_h,
      ImgID.UI_ConfirmSubstrate,
      {},
      true
    );
    substrate.setOnClick( () => { this.onSubstrateClick(); } );
    this.addChild( substrate );

    const text_x = sub_x + sub_w + text_gap;

    const design_confirm_font_size = 47;
    const design_confirm_outline_w = 10;

    const confirm_text = new UITextElement(
      this.ctx,
      text_x,
      sub_y,
      text_w,
      sub_h,
      {
        localization_key: "DELETE_ACCOUNT_DIALOG.CONFIRM_TEXT",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_confirm_font_size ),
        font_weight: 700,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_confirm_outline_w ),
        outline_color: ColorScheme.settings_dialog_title_outline,
      }
    );
    this.addChild( confirm_text, true );

    const design_mark_size = 102;
    const design_mark_x_offset = 18;
    const design_mark_y = 831;

    const mark_w = LayoutScaler.widthFromDesign( design_mark_size );
    const mark_h = LayoutScaler.heightFromDesign( design_mark_size );
    const mark_x = sub_x - LayoutScaler.widthFromDesign( design_mark_x_offset );
    const mark_y = LayoutScaler.yFromDesign( design_mark_y );

    this.mark_image = new UIImage(
      this.ctx,
      mark_x,
      mark_y,
      mark_w,
      mark_h,
      ImgID.UI_ConfirmMark,
      { image_opacity: 0 }
    );
    this.addChild( this.mark_image, true );
  }

  private onSubstrateClick(): void
  {
    this.is_confirmed = !this.is_confirmed;
    audioController.playSound( AudioClips.UI_CLICK );
    this.mark_image.setImageOpacity( this.is_confirmed ? 1 : 0 );
    this.forceRedraw();
    this.updateDeleteBtnState();
  }

  private updateDeleteBtnState(): void
  {
    if ( this.countdown_last_shown !== 0 ) return;

    if ( !this.is_confirmed )
    {
      this.delete_btn.setColor( "#808080" );
      this.delete_btn.setOnClick( () => {} );
    }
    else
    {
      this.delete_btn.setColor( ColorScheme.red );
      this.delete_btn.setOnClick( () =>
      {
        audioController.playSound( AudioClips.UI_CLICK );
        void this.performDeleteAccount();
      } );
    }

    this.delete_btn.forceRedraw();
    this.forceRedraw();
  }

  private async performDeleteAccount(): Promise<void>
  {
    if ( this.is_deleting ) return;
    this.is_deleting = true;

    try
    {
      await deleteAccount();
    }
    catch ( e )
    {
      this.is_deleting = false;
      console.warn( '[DeleteAccountDialog] delete failed:', e );
      DialogManager.openDialog( AuthMessageDialog.forAccountError( e, "AUTH_ERROR.DELETE_FAILED" ) );
      return;
    }

    await Promise.allSettled( [
      GoogleSignInPlugin.signOut(),
      FacebookSignInPlugin.signOut(),
      applePlugin().signOut(),
    ] );

    localStorage.removeItem( 'web_google_id' );
    localStorage.removeItem( 'web_facebook_id' );
    localStorage.removeItem( 'native_apple_id' );
    localStorage.removeItem( 'analytics_userid' );
    localStorage.removeItem( 'player_nickname' );
    localStorage.removeItem( 'player_avatar_id' );
    localStorage.removeItem( 'player_facebook_avatar_url' );
    clearFacebookAvatarCache();
    StatisticsStorage.remove();
    OfflineJournal.clear();
    AuthSessionStore.clear();

    CumulativeBoosterService.reset();
    resetLinkedSocialProviders();

    DialogManager.closeAllDialogs();
    DialogManager.openDialog( new DeleteAccountNotification( async () =>
    {
      await performLogin();
      await ConfigSyncService.refreshAll( 'account_switch' );
      ScreenManager.showScreen( Screens.MENU );
    } ) );
  }

  private expandDialog(): void
  {
    if ( this.is_expanded ) return;
    this.is_expanded = true;

    const newBodyH = LayoutScaler.heightFromDesign( DeleteAccountDialog.BODY_H_EXPANDED_DESIGN );

    const currentPos = this.pixelPosImportant();
    const newBodyY = ( DialogManager.getHeight() - newBodyH ) / 2;
    this.setPixelPos( new Vector2( currentPos.x, newBodyY ), true );

    this.rect.resize( this.rect.getW(), newBodyH );
    this.bg.setHeight( newBodyH );

    const btn_y = LayoutScaler.yFromDesign( DeleteAccountDialog.BTN_Y_EXPANDED_DESIGN );

    const cancelPos = this.cancel_btn.pixelPosImportant();
    this.cancel_btn.setPixelPos( new Vector2( cancelPos.x, btn_y ), true );

    const deletePos = this.delete_btn.pixelPosImportant();
    this.delete_btn.setPixelPos( new Vector2( deletePos.x, btn_y ), true );

    this.delete_btn.setColor( "#808080" );
    this.delete_btn.setOnClick( () => {} );
    this.countdown_start = performance.now();

    this.createConfirmation();
    this.forceRedraw();
  }

  public override update( now: number ): void
  {
    if ( this.countdown_start >= 0 )
    {
      const elapsed   = now - this.countdown_start;
      const remaining = Math.ceil( ( DeleteAccountDialog.COUNTDOWN_SECONDS * 1000 - elapsed ) / 1000 );
      const display   = Math.max( 0, remaining );

      if ( display !== this.countdown_last_shown )
      {
        this.countdown_last_shown = display;
        this.delete_btn.forceRedraw();
        this.forceRedraw();

        if ( display === 0 )
        {
          this.countdown_start = -1;
          this.delete_btn_label.setLocalizationKey( "DELETE_ACCOUNT_DIALOG.BUTTON_TEXT" );
          this.updateDeleteBtnState();
        }
        else
        {
          this.delete_btn_label.setText( String( display ) );
        }
      }
    }

    super.update( now );
  }

  public name(): string { return "DeleteAccountDialog"; }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }
}
